import { fetchApiJson, trackError, trackUserEvent } from '@vend/utilities'

type BulkCancelPaymentRequestsResponse = {
  success: boolean
  error?: string
}

/**
 * Cancel all payment requests for a sale via the invoicing proxy
 *
 * @param saleId {string} The sale ID (documentRef) for which to cancel payment requests
 * @param customerRef {string} The customer reference ID
 * @return {Promise<BulkCancelPaymentRequestsResponse>} A promise resolved when payment requests have been cancelled
 */
export const bulkCancelPaymentRequests = async (
  saleId: string,
  customerRef: string
): Promise<BulkCancelPaymentRequestsResponse> => {
  if (!saleId || !customerRef) {
    return {
      success: false,
      error: 'SaleID and customerRef are required to cancel payment requests.',
    }
  }

  try {
    const params = new URLSearchParams({
      documentRef: saleId,
      customerRef,
    })

    await fetchApiJson(
      `/api/2.0/invoicing/payment-requests/cancel?${params.toString()}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    // Track successful bulk payment requests cancellation
    trackUserEvent('Web Register', 'Bulk Payment Requests Cancelled', {
      saleId,
      customerRef,
    })

    return {
      success: true,
    }
  } catch (err: any) {
    // Track and log the error
    const errorMessage = err?.message || 'Unknown error occurred'
    trackError('Bulk Payment Requests Cancellation Error', errorMessage, {
      saleId,
      customerRef,
    })

    return {
      success: false,
      error: errorMessage,
    }
  }
}
